// Übernahme bestehender Vorlagen in den Baukasten (Bauplan 3.10, Schritt 8): Vergleich der Vorschau vorher und nachher.
import { CONTRACT_ROLES, type Block, type ContractRole, type CustomVariableDef, type Language } from "@talentkontor/domain";
import { exampleValues, renderDraft, type DraftInput } from "../pruefen";
import { speicherFelder } from "./modell";
import { t } from "../../../../lib/sprache";

export interface Bestand {
  body_html: string;
  annex_html: string | null;
  annex_hidden_from: ContractRole[];
  variables: CustomVariableDef[];
  required_signers: ContractRole[];
  language: Language;
}

export interface RollenVergleich { rolle: ContractRole; gleich: boolean; vorher: { html: string; annexHtml: string | null }; nachher: { html: string; annexHtml: string | null } }
export type Unterschied = "text" | "anlage" | "angaben" | "parteien" | "sichtbarkeit";
/**
 * `gleich`: Vorschau je Rolle gleich. `quelleGleich`: gespeicherte Vorlage Zeichen für Zeichen gleich. `ok`: beides; nur dann
 * zeigt der Baukasten „✓ gleich“ (Befund 2 Schritt 8: Parteien können sich ändern, ohne dass die Vorschau es zeigt).
 */
export interface Vergleich { gleich: boolean; quelleGleich: boolean; ok: boolean; unterschiede: Unterschied[]; rollen: RollenVergleich[] }

/** Leerraum spielt im gedruckten Vertrag keine Rolle (HTML): zwischen Tags weg, sonst ein Leerzeichen. */
export const normalHtml = (s: string | null) => (s ?? "").replace(/>\s+</g, "><").replace(/\s+/g, " ").trim();

/**
 * Vorschau der Bestandsvorlage gegen die umgewandelten Bausteine, je Rolle mit denselben Beispielwerten. `quelleGleich`:
 * Text, vertraulicher Teil, Angaben und Parteien sind Zeichen für Zeichen gleich (dann bleiben auch die Prüfwerte gleich).
 */
export function vergleicheUmwandlung(v: Bestand, blocks: readonly Block[], beispiel: Omit<DraftInput, "body" | "annex" | "defs" | "values">): Vergleich {
  const f = speicherFelder(blocks, v.variables, v.language);
  const menge = (xs: readonly string[]) => [...xs].sort().join();
  const unterschiede: Unterschied[] = [];
  if (f.body_html !== v.body_html) unterschiede.push("text");
  if ((f.annex_html ?? null) !== (v.annex_html ?? null)) unterschiede.push("anlage");
  if (JSON.stringify(f.variables) !== JSON.stringify(v.variables)) unterschiede.push("angaben");
  if (menge(f.required_signers) !== menge(v.required_signers)) unterschiede.push("parteien");
  if (menge(f.annex_hidden_from) !== menge(v.annex_hidden_from)) unterschiede.push("sichtbarkeit");
  const quelleGleich = unterschiede.length === 0;
  const rollen = CONTRACT_ROLES.filter((r) => r === "agency" || v.required_signers.includes(r) || f.required_signers.includes(r)).map((rolle) => {
    const a = renderDraft({ body: v.body_html, annex: v.annex_html, defs: v.variables, values: exampleValues(v.variables), ...beispiel }, rolle, v.annex_hidden_from, v.required_signers);
    const b = renderDraft({ body: f.body_html, annex: f.annex_html, defs: f.variables, values: exampleValues(f.variables), ...beispiel }, rolle, f.annex_hidden_from, f.required_signers);
    const gleich = normalHtml(a.html) === normalHtml(b.html) && normalHtml(a.annexHtml) === normalHtml(b.annexHtml);
    return { rolle, gleich, vorher: { html: a.html, annexHtml: a.annexHtml }, nachher: { html: b.html, annexHtml: b.annexHtml } };
  });
  const gleich = rollen.every((r) => r.gleich);
  return { gleich, quelleGleich, ok: gleich && quelleGleich, unterschiede, rollen };
}

export const UNTERSCHIED_TEXT: Record<Unterschied, () => string> = {
  text: () => t("Text", "text"),
  anlage: () => t("vertraulicher Teil", "confidential part"),
  angaben: () => t("Angaben", "fields"),
  parteien: () => t("wer unterschreibt", "who signs"),
  sichtbarkeit: () => t("wer den vertraulichen Teil sieht", "who sees the confidential part"),
};
